/**
 * The Execution Dashboard's chart helpers — §17.3.
 *
 * Cloned from `ai_readiness_template/views/execution-dashboard.ejs:327-492`.
 *
 * NO CHART LIBRARY. Every chart is inline SVG built by a small function. That is the source's
 * own decision and it holds here for the same reasons plus one more: this platform refuses CDN
 * dependencies (the artifact CSP blocks them outright), and hand-drawn SVG keeps the screen
 * self-contained and printable.
 *
 * Geometry — radii, padding, stroke widths, the 150% y-ceiling on the bubble chart, the
 * `Math.sqrt` bubble radius — is the source's and is not adjusted.
 */

import type { ReactNode } from 'react';

export interface Part {
  k: string;
  v: number;
  c: string;
}

export const money = (n: number) => `$${Math.round(n || 0).toLocaleString('en-US')}`;
export const kmoney = (n: number) => {
  const a = Math.abs(n || 0);
  if (a >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (a >= 1_000) return `$${Math.round(n / 1_000)}k`;
  return `$${Math.round(n || 0)}`;
};
export const fmtNum = (n: number) => (Math.round((n || 0) * 10) / 10).toLocaleString();
/** Utilisation colour: green under 85, amber to 100, red over. Null = nothing planned. */
export const util = (p: number | null) =>
  p === null ? 'var(--faint)' : p > 100 ? 'var(--bad)' : p >= 85 ? 'var(--warn)' : 'var(--good)';

export const TYPES = ['Deliverable', 'Activity', 'Unclassified'] as const;
export const TCOL: Record<string, string> = {
  Deliverable: '#1976d2',
  Activity: '#c2185b',
  Unclassified: '#b0bec5',
};

/** Days between two ISO dates. */
export const daysBetween = (a: string, b: string) =>
  Math.round((new Date(`${a}T00:00:00`).getTime() - new Date(`${b}T00:00:00`).getTime()) / 86400000);

/* ── donut ─────────────────────────────────────────────────────────────────────────────── */
export function Donut({
  parts,
  size,
  hole,
  centre,
}: {
  parts: Part[];
  size: number;
  hole: number;
  centre?: { top: ReactNode; bot: string };
}) {
  const total = parts.reduce((s, p) => s + p.v, 0) || 1;
  const r = size / 2;
  const R = r - 2;
  const ir = R * hole;
  let a0 = -Math.PI / 2;
  const paths = parts.map((p) => {
    const a1 = a0 + (p.v / total) * Math.PI * 2;
    const big = a1 - a0 > Math.PI ? 1 : 0;
    const d =
      `M${r + R * Math.cos(a0)} ${r + R * Math.sin(a0)}` +
      ` A${R} ${R} 0 ${big} 1 ${r + R * Math.cos(a1)} ${r + R * Math.sin(a1)}` +
      ` L${r + ir * Math.cos(a1)} ${r + ir * Math.sin(a1)}` +
      ` A${ir} ${ir} 0 ${big} 0 ${r + ir * Math.cos(a0)} ${r + ir * Math.sin(a0)} Z`;
    const el = p.v > 0 ? <path d={d} fill={p.c} key={p.k}><title>{`${p.k}: ${p.v}`}</title></path> : null;
    a0 = a1;
    return el;
  });
  const m = centre ?? { top: total, bot: '' };
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      {paths}
      <text x="50%" y="50%" textAnchor="middle" dy="-2" fontSize={size * 0.2} fontWeight="700" fill="#263238">
        {m.top}
      </text>
      <text x="50%" y="50%" textAnchor="middle" dy={size * 0.16} fontSize={size * 0.088} fill="#90a4ae">
        {m.bot}
      </text>
    </svg>
  );
}

/* ── stacked bars ──────────────────────────────────────────────────────────────────────── */
export function Stack({
  rows,
  keys,
  colours,
  w,
  labelW = 132,
}: {
  rows: Record<string, unknown>[];
  keys: readonly string[];
  colours: Record<string, string>;
  w: number;
  labelW?: number;
}) {
  let max = 1;
  rows.forEach((r) => {
    let t = 0;
    keys.forEach((k) => (t += Number(r[k]) || 0));
    if (t > max) max = t;
  });
  return (
    <>
      {rows.map((r, ri) => {
        let tot = 0;
        keys.forEach((k) => (tot += Number(r[k]) || 0));
        let x = 0;
        const segs = keys.map((k) => {
          const v = Number(r[k]) || 0;
          const wpx = (v / max) * w;
          const el = v ? (
            <rect key={k} x={x} y="0" width={Math.max(wpx, 1.5)} height="18" rx="2" fill={colours[k]}>
              <title>{`${k}: ${v}`}</title>
            </rect>
          ) : null;
          x += wpx;
          return el;
        });
        return (
          <div
            key={ri}
            style={{
              display: 'grid',
              gridTemplateColumns: `${labelW}px 1fr 40px`,
              alignItems: 'center',
              gap: 10,
              marginBottom: 7,
            }}
          >
            <span
              style={{
                fontSize: 12,
                color: '#546e7a',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {String(r.label)}
            </span>
            <svg width={w} height="18" viewBox={`0 0 ${w} 18`}>
              {segs}
            </svg>
            <span className="num" style={{ fontSize: 12, fontWeight: 700, color: '#37474f', textAlign: 'right' }}>
              {tot}
            </span>
          </div>
        );
      })}
    </>
  );
}

/* ── columns ───────────────────────────────────────────────────────────────────────────── */
export function Cols({
  rows,
  w,
  h,
  colour,
  keyName = 'bucket',
}: {
  rows: Record<string, unknown>[];
  w: number;
  h: number;
  colour: string | ((r: Record<string, unknown>) => string);
  keyName?: string;
}) {
  let max = 1;
  rows.forEach((r) => {
    if (Number(r.n) > max) max = Number(r.n);
  });
  const step = w / (rows.length || 1);
  const bw = Math.max(16, step - 18);
  return (
    <svg width={w} height={h + 28} viewBox={`0 0 ${w} ${h + 28}`}>
      {rows.map((r, i) => {
        const n = Number(r.n) || 0;
        const x = i * step + (step - bw) / 2;
        const bh = (n / max) * h;
        const c = typeof colour === 'function' ? colour(r) : colour;
        return (
          <g key={i}>
            <rect x={x} y={h - bh} width={bw} height={Math.max(bh, n ? 2 : 0)} rx="3" fill={c}>
              <title>{`${String(r[keyName])}: ${n}`}</title>
            </rect>
            <text x={x + bw / 2} y={h - bh - 5} textAnchor="middle" fontSize="11" fontWeight="700" fill="#37474f">
              {n || ''}
            </text>
            <text x={x + bw / 2} y={h + 17} textAnchor="middle" fontSize="10.5" fill="#90a4ae">
              {String(r[keyName])}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

/* ── funnel ────────────────────────────────────────────────────────────────────────────── */
export function Funnel({ stages }: { stages: { k: string; n: number; c: string }[] }) {
  let max = 1;
  stages.forEach((s) => {
    if (s.n > max) max = s.n;
  });
  return (
    <>
      {stages.map((s) => (
        <div
          key={s.k}
          style={{ display: 'grid', gridTemplateColumns: '96px 1fr 30px', alignItems: 'center', gap: 10, marginBottom: 9 }}
        >
          <span style={{ fontSize: 12, fontWeight: 600, color: '#546e7a' }}>{s.k}</span>
          <div style={{ height: 26, background: '#f2f5f7', borderRadius: 5, overflow: 'hidden' }}>
            <div
              style={{
                height: '100%',
                width: `${(s.n / max) * 100}%`,
                background: `linear-gradient(90deg,${s.c}22,${s.c})`,
                borderRadius: 5,
              }}
            />
          </div>
          <span className="num" style={{ fontSize: 13, fontWeight: 700, color: '#263238', textAlign: 'right' }}>
            {s.n}
          </span>
        </div>
      ))}
    </>
  );
}

/* ── the S-curve: cumulative planned vs delivered ──────────────────────────────────────── */
export function SCurve({ rows, w, h }: { rows: { ym: string; n: number; done: number }[]; w: number; h: number }) {
  if (!rows?.length) return <div className="exd-dim">No delivered-by dates recorded.</div>;
  const padL = 34, padR = 10, padT = 10, padB = 26;
  let cp = 0, cd = 0;
  const pts = rows.map((r) => {
    cp += r.n;
    cd += r.done;
    return { ym: r.ym, p: cp, d: cd };
  });
  const max = Math.max(1, cp);
  const X = (i: number) => padL + (i / Math.max(1, pts.length - 1)) * (w - padL - padR);
  const Y = (v: number) => h - padB - (v / max) * (h - padB - padT);
  const line = (k: 'p' | 'd', c: string) => (
    <polyline
      fill="none"
      stroke={c}
      strokeWidth="2.5"
      strokeLinejoin="round"
      points={pts.map((p, i) => `${X(i)},${Y(p[k])}`).join(' ')}
    />
  );
  const area = `M${X(0)},${Y(0)} ${pts.map((p, i) => `L${X(i)},${Y(p.p)}`).join(' ')} L${X(pts.length - 1)},${Y(0)} Z`;
  const every = Math.ceil(pts.length / 6);
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`}>
      {[0, 0.25, 0.5, 0.75, 1].map((f) => (
        <g key={f}>
          <line x1={padL} y1={Y(max * f)} x2={w - padR} y2={Y(max * f)} stroke="#f2f5f7" />
          <text x={padL - 6} y={Y(max * f) + 3} textAnchor="end" fontSize="9.5" fill="#b0bec5">
            {Math.round(max * f)}
          </text>
        </g>
      ))}
      <path d={area} fill="#1976d2" fillOpacity=".07" />
      {line('p', '#90a4ae')}
      {line('d', '#2e7d32')}
      {pts.map((p, i) => (
        <circle cx={X(i)} cy={Y(p.d)} r="3" fill="#2e7d32" key={p.ym}>
          <title>{`${p.ym}: ${p.d} done of ${p.p} due`}</title>
        </circle>
      ))}
      {pts.map((p, i) =>
        i % every === 0 ? (
          <text x={X(i)} y={h - 8} textAnchor="middle" fontSize="9.5" fill="#90a4ae" key={`t${p.ym}`}>
            {p.ym.slice(2)}
          </text>
        ) : null,
      )}
    </svg>
  );
}

/* ── the bubble chart: completion (x) vs budget used (y), radius = work items ───────────── */
export function Bubble({
  rows,
  w,
  h,
}: {
  rows: { id: string; completion: number; budgetUtil: number | null; wiTotal: number; status: string }[];
  w: number;
  h: number;
}) {
  if (!rows.length) return <div className="exd-dim">Nothing to plot yet.</div>;
  const padL = 36, padR = 14, padT = 12, padB = 30;
  let maxR = 1;
  rows.forEach((r) => {
    if (r.wiTotal > maxR) maxR = r.wiTotal;
  });
  const X = (v: number) => padL + (v / 100) * (w - padL - padR);
  /* The y-axis tops out at 150%: past that the point is "badly over" and the exact figure
     stops mattering for the shape of the chart. */
  const Y = (v: number) => h - padB - (Math.min(v, 150) / 150) * (h - padB - padT);
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`}>
      {/* The pink band above 100% is overrun. */}
      <rect x={X(0)} y={Y(150)} width={X(100) - X(0)} height={Y(100) - Y(150)} fill="#ffebee" fillOpacity=".5" />
      <line x1={X(0)} y1={Y(100)} x2={X(100)} y2={Y(100)} stroke="#ef9a9a" strokeDasharray="4 4" />
      {/* The dashed diagonal marks "spending in step with delivering". */}
      <line x1={X(0)} y1={Y(0)} x2={X(100)} y2={Y(100)} stroke="#cfd8dc" strokeDasharray="4 4" />
      <line x1={padL} y1={h - padB} x2={w - padR} y2={h - padB} stroke="#eceff1" />
      <line x1={padL} y1={padT} x2={padL} y2={h - padB} stroke="#eceff1" />
      {rows.map((p) => {
        const r = 5 + 11 * Math.sqrt(p.wiTotal / maxR);
        const y = p.budgetUtil == null ? Y(0) : Y(p.budgetUtil);
        const c = p.status === 'At Risk' ? '#c62828' : p.status === 'Complete' ? '#2e7d32' : '#1976d2';
        return (
          <circle cx={X(p.completion)} cy={y} r={r} fill={c} fillOpacity=".55" stroke={c} strokeWidth="1.5" key={p.id}>
            <title>{`${p.id} — ${p.completion}% complete, ${
              p.budgetUtil == null ? 'no budget' : `${p.budgetUtil}% spent`
            }, ${p.wiTotal} work items`}</title>
          </circle>
        );
      })}
      <text x={w / 2} y={h - 8} textAnchor="middle" fontSize="10" fill="#90a4ae">
        completion %
      </text>
      <text x="11" y={h / 2} textAnchor="middle" fontSize="10" fill="#90a4ae" transform={`rotate(-90 11 ${h / 2})`}>
        budget spent %
      </text>
    </svg>
  );
}

/* ── waterfall: budget variance, project by project ────────────────────────────────────── */
export function Waterfall({
  rows,
  w,
  h,
}: {
  rows: { id: string; planned: number; actual: number; variance: number }[];
  w: number;
  h: number;
}) {
  const items = rows.filter((r) => r.planned || r.actual);
  if (!items.length) return <div className="exd-dim">No budget recorded.</div>;
  let total = 0, span = 1;
  items.forEach((r) => {
    total += r.variance;
    if (Math.abs(r.variance) > span) span = Math.abs(r.variance);
  });
  if (Math.abs(total) > span) span = Math.abs(total);
  const padL = 8, padR = 8, padB = 34;
  const step = (w - padL - padR) / (items.length + 1);
  const bw = Math.max(18, step - 16);
  const mid = (h - padB) / 2 + 4;
  const Y = (v: number) => mid - (v / span) * ((h - padB) / 2 - 6);
  const all = [...items.map((r) => ({ ...r, _t: false })), { id: 'Portfolio', variance: total, _t: true }];
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`}>
      <line x1={padL} y1={mid} x2={w - padR} y2={mid} stroke="#cfd8dc" />
      {all.map((r, i) => {
        const x = padL + i * step + (step - bw) / 2;
        const y0 = Y(0);
        const y1 = Y(r.variance);
        const c = r._t ? '#455a64' : r.variance > 0 ? '#c62828' : '#2e7d32';
        return (
          <g key={`${r.id}${i}`}>
            <rect x={x} y={Math.min(y0, y1)} width={bw} height={Math.max(2, Math.abs(y1 - y0))} rx="2" fill={c}>
              <title>{`${r.id}: ${r.variance > 0 ? '+' : ''}${money(r.variance)}`}</title>
            </rect>
            <text x={x + bw / 2} y={h - 18} textAnchor="middle" fontSize="9.5" fill="#90a4ae">
              {String(r.id).slice(0, 11)}
            </text>
            <text x={x + bw / 2} y={h - 6} textAnchor="middle" fontSize="9.5" fontWeight="700" fill={c}>
              {`${r.variance > 0 ? '+' : ''}${kmoney(r.variance)}`}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

/* ── ring ──────────────────────────────────────────────────────────────────────────────── */
export function Ring({
  pct,
  size,
  colour,
  label,
  sub,
}: {
  pct: number;
  size: number;
  colour: string;
  label: string;
  sub?: string;
}) {
  const r = (size - 9) / 2;
  const c = 2 * Math.PI * r;
  const off = c * (1 - (pct || 0) / 100);
  const cx = size / 2;
  return (
    <div style={{ textAlign: 'center' }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle cx={cx} cy={cx} r={r} fill="none" stroke="#eceff1" strokeWidth="8" />
        <circle
          cx={cx}
          cy={cx}
          r={r}
          fill="none"
          stroke={colour}
          strokeWidth="8"
          strokeLinecap="round"
          strokeDasharray={c}
          strokeDashoffset={off}
          transform={`rotate(-90 ${cx} ${cx})`}
        />
        <text x="50%" y="50%" textAnchor="middle" dy="0.35em" fontSize={size * 0.25} fontWeight="700" fill="#263238">
          {pct || 0}%
        </text>
      </svg>
      <div style={{ fontSize: 11, color: '#546e7a', marginTop: 5, fontWeight: 600 }}>{label}</div>
      <div style={{ fontSize: 10.5, color: '#b0bec5' }}>{sub ?? ''}</div>
    </div>
  );
}

/* ── risk heat map: severity × status ──────────────────────────────────────────────────── */
export function Heat({ rows }: { rows: Record<string, unknown>[] }) {
  const sts = ['Open', 'Watch', 'Mitigating', 'Resolved', 'Closed'];
  let max = 1;
  rows.forEach((r) => sts.forEach((s) => { if (Number(r[s] ?? 0) > max) max = Number(r[s]); }));
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '76px repeat(5,1fr)', gap: 4, alignItems: 'center' }}>
      <span />
      {sts.map((s) => (
        <span
          key={s}
          style={{ fontSize: 9.5, color: '#90a4ae', textAlign: 'center', textTransform: 'uppercase', letterSpacing: '.04em' }}
        >
          {s}
        </span>
      ))}
      {rows.map((r) => {
        const sev = String(r.sev);
        return (
          <Fragmentish key={sev}>
            <span style={{ fontSize: 12, fontWeight: 600, color: '#546e7a' }}>{sev}</span>
            {sts.map((s) => {
              const v = Number(r[s] ?? 0);
              const a = v ? 0.18 + 0.82 * (v / max) : 0;
              const c = sev === 'Critical' || sev === 'High' ? '198,40,40' : sev === 'Medium' ? '245,159,0' : '120,144,156';
              return (
                <div
                  key={s}
                  style={{
                    height: 38,
                    borderRadius: 6,
                    background: `rgba(${c},${a})`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 13,
                    fontWeight: 700,
                    color: a > 0.5 ? '#fff' : '#b0bec5',
                  }}
                >
                  {v || '·'}
                </div>
              );
            })}
          </Fragmentish>
        );
      })}
    </div>
  );
}
/* A grid's children must be siblings, so the row label and its five cells cannot sit inside a
   wrapper element — only a fragment. Named so the intent survives a later tidy-up. */
function Fragmentish({ children }: { children: ReactNode }) {
  return <>{children}</>;
}

/* ── horizontal bars ───────────────────────────────────────────────────────────────────── */
export function HBars({
  rows,
  colourFn,
  fmt,
}: {
  rows: { k: string; v: number }[];
  colourFn?: (r: { k: string; v: number }) => string;
  fmt?: (r: { k: string; v: number }) => string;
}) {
  let max = 1;
  rows.forEach((r) => {
    if (r.v > max) max = r.v;
  });
  return (
    <>
      {rows.map((r) => (
        <div
          key={r.k}
          style={{ display: 'grid', gridTemplateColumns: '150px 1fr 96px', alignItems: 'center', gap: 10, marginBottom: 8 }}
        >
          <span style={{ fontSize: 12.5, color: '#546e7a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {r.k}
          </span>
          <div className="exd-bar-tr" style={{ height: 12 }}>
            <div className="exd-bar-fl" style={{ width: `${(r.v / max) * 100}%`, background: colourFn ? colourFn(r) : 'var(--accent)' }} />
          </div>
          <span className="num" style={{ fontSize: 12, fontWeight: 600, textAlign: 'right' }}>
            {fmt ? fmt(r) : r.v}
          </span>
        </div>
      ))}
    </>
  );
}

/* ── KPI card ──────────────────────────────────────────────────────────────────────────── */
export function Kpi({ lbl, val, sub, c }: { lbl: string; val: ReactNode; sub?: ReactNode; c?: string }) {
  return (
    <div className="exd-kpi" style={{ '--k': c ?? 'transparent' } as React.CSSProperties}>
      <div className="lbl">{lbl}</div>
      <div className="val num">{val}</div>
      <div className="sub">{sub ?? ''}</div>
    </div>
  );
}
